import { ParamMap, Params } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { PaginationMetaDto } from '../dto';
import { PAGINATION_DEFAULTS } from '../constants/pagination.constants';
import { PaginatedListQuery } from '../interfaces/paginated-list-query.interface';
import { EnSortDirection } from '../enums/sort-direction.enum';

export const PAGINATED_QUERY_PARAM = 'paginated';
export const LIST_DELETED_PARAM = 'deleted';

export type PaginatedListResponse<T> = T[] | { data: T[]; meta: PaginationMetaDto };

/** Converts a domain list query into flat HTTP query params for a paginated backend read. */
export function toPaginationParams(query: PaginatedListQuery): Record<string, string> {
  const params: Record<string, string> = {
    [PAGINATED_QUERY_PARAM]: 'true',
  };

  if (query.page != null) {
    params['page'] = String(query.page);
  }
  if (query.limit != null) {
    params['limit'] = String(query.limit);
  }

  if (query.sortBy) {
    params['sortBy'] = query.sortBy;
  }

  if (query.sortDirection) {
    params['sortDirection'] = query.sortDirection;
  }

  if (query.search) {
    params['search'] = query.search;
  }

  if (query.deleted === true) {
    params[LIST_DELETED_PARAM] = 'true';
  }

  return params;
}

export function isPaginatedListResponse<T>(
  value: PaginatedListResponse<T>,
): value is { data: T[]; meta: PaginationMetaDto } {
  return Array.isArray(value) === false && value != null && 'data' in value && 'meta' in value;
}

export function mapListLoadResult<T>(response: PaginatedListResponse<T>): { data: T[]; total: number } {
  if (isPaginatedListResponse(response)) {
    return { data: response.data, total: response.meta.total };
  }
  return { data: response, total: response.length };
}

/**
 * Normalizes a list response into `{ data, total }`. When a paginated `query`
 * was sent the server must answer with the `{ data, meta }` envelope; if it
 * doesn't we throw so the caller's `catchError` surfaces a failure rather than
 * silently treating a bare array as a page.
 */
export function resolveListResult<T>(
  response: PaginatedListResponse<T>,
  query?: PaginatedListQuery | null,
): { data: T[]; total: number } {
  if (query && !isPaginatedListResponse(response)) {
    throw new Error('Expected a paginated list response');
  }
  return mapListLoadResult(response);
}

/**
 * Shared body for a store's list-read effect: normalizes the response via
 * {@link resolveListResult} and maps it to success/failure actions. Keeps every
 * list effect identical so the paginated/array handling lives in exactly one place.
 */
export function listResultActions$<T, S, F>(
  source$: Observable<PaginatedListResponse<T>>,
  query: PaginatedListQuery | null | undefined,
  onSuccess: (result: { data: T[]; total: number }) => S,
  onFailure: (error: unknown) => F,
): Observable<S | F> {
  return source$.pipe(
    map((response): S | F => onSuccess(resolveListResult(response, query))),
    catchError((error) => of(onFailure(error))),
  );
}

/**
 * Stringifies a flat filter record into HTTP query params, dropping `null`/
 * `undefined`. Lets services forward typed filter DTOs (including `boolean`
 * flags) through `options.params` without per-field plumbing.
 */
export function toHttpParams(
  record: Record<string, string | number | boolean | null | undefined>,
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined && value !== null) {
      params[key] = String(value);
    }
  }
  return params;
}

/** Reads the trash scope out of route query params. One spelling, three readers. */
export function isDeletedScope(params: ParamMap): boolean {
  return params.get(LIST_DELETED_PARAM) === 'true';
}

/**
 * The trash flag as HTTP query params, for the list reads that are NOT paginated
 * and so cannot go through {@link toPaginationParams}.
 *
 * Keeps the param name in one place: three effects were spelling it by hand, in
 * three different ways, for the same question.
 */
export function listDeletedParams(deleted?: boolean): Record<string, string> {
  return deleted === true ? { [LIST_DELETED_PARAM]: 'true' } : {};
}

/** PrimeNG `first`/`rows` to a 1-based page number. */
export function pageFromFirst(first: number, rows: number): number {
  return Math.floor((first ?? 0) / (rows || PAGINATION_DEFAULTS.LIMIT)) + 1;
}

/** 1-based page to PrimeNG `first` offset. */
export function firstFromQuery(query: PaginatedListQuery): number {
  const page = query.page ?? PAGINATION_DEFAULTS.PAGE;
  const limit = query.limit ?? PAGINATION_DEFAULTS.LIMIT;
  return (page - 1) * limit;
}

/**
 * Reads a paginated list query from route query params, applying defaults when
 * absent/invalid. The URL is the source of truth so reloads/deep-links are
 * deterministic.
 */
export function paginatedQueryFromParams(params: ParamMap): PaginatedListQuery {
  const page = Number(params.get('page'));
  const limit = Number(params.get('limit'));
  const sortDirection = params.get('sortDirection');

  return {
    page: Number.isFinite(page) && page > 0 ? page : PAGINATION_DEFAULTS.PAGE,
    limit: Number.isFinite(limit) && limit > 0 ? limit : PAGINATION_DEFAULTS.LIMIT,
    sortBy: params.get('sortBy') || undefined,
    sortDirection:
      sortDirection === EnSortDirection.ASC || sortDirection === EnSortDirection.DESC
        ? (sortDirection as EnSortDirection)
        : undefined,
    search: params.get('search') || undefined,
    deleted: isDeletedScope(params) ? true : undefined,
  };
}

/**
 * Serializes a paginated list query into route query params. Empty fields are
 * set to `null` so `queryParamsHandling: 'merge'` removes them from the URL.
 */
export function paginatedQueryToRouteParams(query: PaginatedListQuery): Params {
  return {
    page: query.page ?? PAGINATION_DEFAULTS.PAGE,
    limit: query.limit ?? PAGINATION_DEFAULTS.LIMIT,
    sortBy: query.sortBy ?? null,
    sortDirection: query.sortDirection ?? null,
    search: query.search || null,
    [LIST_DELETED_PARAM]: query.deleted === true ? true : null,
  };
}
